/**
 * God-admin editable AG Grid controller for the AI Voter List page.
 *
 * Extends AgGridBaseController (shared toolkit — untouched) and adds:
 *   - Client-side row model loaded from `rowDataUrlValue`
 *   - Inline editing via AG Grid's `editable` ColDef + `onCellValueChanged`
 *   - PATCH to `updateUrlTemplateValue` (`:id` replaced with row id) on change
 *   - On success: refresh the row with the server-returned data (picks up
 *     has_error / has_warning row-class changes)
 *   - On failure: rollback cell to oldValue and surface the error
 *   - rowClassRules wired to the `row_status` field set by GridRowSerializer
 *     (error / needs_review / warning)
 *   - "Needs review only" external filter driven by a checkbox target
 *   - `ai_status` cells rendered through the server-translated labels in `i18nValue`
 *   - `validate` action: POSTs to `validateUrlValue`, replaces full row set
 *     (empty rows may be deleted server-side), shows valid/error summary
 */

import {
  createGrid,
  GridApi,
  GridOptions,
  ColDef,
  CellValueChangedEvent,
  RowClassRules,
  IRowNode,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../../../application/toolkit/ag_grid_base_component/ag_grid_base_controller";
import { ebTheme } from "../../../application/toolkit/ag_grid_shared/eb_theme";
import { ebFilterParams, ebLocaleText } from "../../../application/toolkit/ag_grid_shared/filter_defaults";
import { NO_CLIENT_EXPORT } from "../../../application/toolkit/ag_grid_shared/no_client_export";

// Strings the server translates (voter_list.ai_importer.grid.*); the English defaults only
// apply if the value is missing, e.g. in a stale cached page.
interface GridStrings {
  statusAutoImport: string;
  statusNeedsReview: string;
  valid: string;
  errorsOne: string;
  errorsOther: string;
  validateFailed: string;
}

const DEFAULT_STRINGS: GridStrings = {
  statusAutoImport: "Auto-imported",
  statusNeedsReview: "Needs review",
  valid: "Valid — no errors",
  errorsOne: "%{count} error found",
  errorsOther: "%{count} errors found",
  validateFailed: "Validation request failed",
};

const NEEDS_REVIEW = "needs_review";
const AUTO_IMPORT = "auto_import";

export default class extends AgGridBaseController {
  static targets = [
    ...AgGridBaseController.targets,
    "grid",
    "validateButton",
    "validateStatus",
    "downloadButton",
    "needsReviewToggle",
    "needsReviewCount",
  ];

  static values = {
    ...AgGridBaseController.values,
    rowDataUrl: String,
    updateUrlTemplate: String,
    validateUrl: String,
    downloadUrl: String,
    i18n: Object,
  };

  declare readonly i18nValue: Partial<GridStrings>;
  declare readonly hasI18nValue: boolean;

  declare readonly rowDataUrlValue: string;
  declare readonly updateUrlTemplateValue: string;
  declare readonly validateUrlValue: string;
  declare readonly hasValidateUrlValue: boolean;
  declare readonly downloadUrlValue: string;
  declare readonly hasDownloadUrlValue: boolean;

  declare readonly validateButtonTarget: HTMLButtonElement;
  declare readonly hasValidateButtonTarget: boolean;
  declare readonly validateStatusTarget: HTMLElement;
  declare readonly hasValidateStatusTarget: boolean;
  declare readonly downloadButtonTarget: HTMLElement;
  declare readonly hasDownloadButtonTarget: boolean;
  declare readonly needsReviewToggleTarget: HTMLInputElement;
  declare readonly hasNeedsReviewToggleTarget: boolean;
  declare readonly needsReviewCountTarget: HTMLElement;
  declare readonly hasNeedsReviewCountTarget: boolean;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  connect() {
    super.connect(); // license key + outside-click handler

    if (!this.hasGridTarget) return;

    const rowClassRules: RowClassRules = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      has_error: (params) =>
        (params.data as Record<string, unknown>)?.row_status === "error",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      has_warning: (params) =>
        (params.data as Record<string, unknown>)?.row_status === "warning",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      needs_review: (params) =>
        (params.data as Record<string, unknown>)?.row_status === NEEDS_REVIEW,
    };

    const options: GridOptions = {
      theme: ebTheme,
      localeText: ebLocaleText,
      columnDefs: this.localizedColumnDefs(),
      rowData: [],
      rowClassRules,
      isExternalFilterPresent: () => this.needsReviewOnly(),
      doesExternalFilterPass: (node: IRowNode) => this.isNeedsReview(node.data),
      domLayout: "autoHeight",
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        filterParams: ebFilterParams,
      },
      stopEditingWhenCellsLoseFocus: true,
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.handleCellValueChanged(event);
      },
      ...NO_CLIENT_EXPORT,
    };

    this.gridApi = createGrid(this.gridTarget, options);

    if (this.rowDataUrlValue) {
      this.fetchRowData();
    }
  }

  disconnect() {
    super.disconnect();
  }

  // Required stubs from the abstract base (no view-toggle needed here)
  switchToBasic(): void { /* not used */ }
  switchToAdvanced(): void { /* not used */ }

  // ── Public actions ─────────────────────────────────────────────────────────

  // Checkbox change: re-run the grid's filters so the external "needs review" filter applies.
  toggleNeedsReview(): void {
    this.gridApi?.onFilterChanged();
  }

  validate(): void {
    if (!this.hasValidateUrlValue || !this.validateUrlValue) return;

    const button = this.hasValidateButtonTarget ? this.validateButtonTarget : null;
    if (button) button.disabled = true;

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";

    fetch(this.validateUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Validate failed: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        const rows = Array.isArray(json.rows) ? json.rows : [];
        // Replace the full row set — validation may have deleted empty rows
        if (this.gridApi) {
          this.gridApi.setGridOption("rowData", rows);
        }
        this.updateNeedsReviewCount(rows);
        this.showValidateStatus(json.valid as boolean, json.error_count as number);
      })
      .catch((error) => {
        console.error("[AiVoterListGridComponent] validate error:", error);
        this.showValidateStatusError();
      })
      .finally(() => {
        if (button) button.disabled = false;
      });
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private get strings(): GridStrings {
    return { ...DEFAULT_STRINGS, ...(this.hasI18nValue ? this.i18nValue : {}) };
  }

  // `ai_status` carries the importer's raw verdict; show it through the translated labels.
  private localizedColumnDefs(): ColDef[] {
    const labels: Record<string, string> = {
      [AUTO_IMPORT]: this.strings.statusAutoImport,
      [NEEDS_REVIEW]: this.strings.statusNeedsReview,
    };

    return (this.columnDefsValue as ColDef[]).map((colDef) =>
      colDef.field === "ai_status"
        ? { ...colDef, valueFormatter: (params) => labels[params.value as string] ?? "" }
        : colDef
    );
  }

  private isNeedsReview(data: unknown): boolean {
    return (data as Record<string, unknown> | undefined)?.row_status === NEEDS_REVIEW;
  }

  private needsReviewOnly(): boolean {
    return this.hasNeedsReviewToggleTarget && this.needsReviewToggleTarget.checked;
  }

  private updateNeedsReviewCount(rows: Array<Record<string, unknown>>): void {
    if (!this.hasNeedsReviewCountTarget) return;
    const count = rows.filter((row) => this.isNeedsReview(row)).length;
    this.needsReviewCountTarget.textContent = count > 0 ? `(${count})` : "";
    this.needsReviewCountTarget.hidden = count === 0;
  }

  private fetchRowData(): void {
    if (!this.gridApi) return;
    this.gridApi.setGridOption("loading", true);

    fetch(this.rowDataUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (!this.gridApi) return;
        const rows = Array.isArray(json) ? json : (json.rows ?? []);
        this.gridApi.setGridOption("rowData", rows);
        this.gridApi.setGridOption("loading", false);
        this.updateNeedsReviewCount(rows);
        this.autoSizeNonFlexColumns();
      })
      .catch((error) => {
        console.error("[AiVoterListGridComponent] fetchRowData error:", error);
        this.gridApi?.setGridOption("loading", false);
      });
  }

  private handleCellValueChanged(event: CellValueChangedEvent): void {
    const { colDef, newValue, oldValue, node } = event;
    const rowId = (node.data as Record<string, unknown>)?.id as string | number;
    const field = colDef.field;

    if (!field || rowId === undefined || rowId === null) return;

    const url = this.updateUrlTemplateValue.replace(":id", String(rowId));
    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify({ [field]: newValue }),
    })
      .then((response) => {
        return response.json().then((json) => ({ ok: response.ok, json }));
      })
      .then(({ ok, json }) => {
        if (ok && json.row) {
          // Apply refreshed row so row_status-driven CSS recolors immediately
          node.setData(json.row as Record<string, unknown>);
        } else {
          // Rollback
          node.setDataValue(field, oldValue);
          const errorMsg = this.extractError(json);
          console.warn("[AiVoterListGridComponent] update rejected:", errorMsg);
          if (errorMsg) {
            this.showRowError(node.data as Record<string, unknown>, errorMsg);
          }
        }
      })
      .catch((error) => {
        node.setDataValue(field, oldValue);
        console.error("[AiVoterListGridComponent] PATCH error:", error);
      });
  }

  private extractError(json: Record<string, unknown>): string {
    if (typeof json.errors === "string") return json.errors;
    if (json.errors && typeof json.errors === "object") {
      return Object.values(json.errors as Record<string, string[]>)
        .flat()
        .join(", ");
    }
    return "Update failed";
  }

  // Lightweight transient error surface: sets a row_status of 'error' so the
  // row flashes red. A full toast/overlay is a Delta improvement.
  private showRowError(
    rowData: Record<string, unknown>,
    _message: string
  ): void {
    if (!this.gridApi) return;
    this.gridApi.forEachNode((node) => {
      const data = node.data as Record<string, unknown>;
      if (data && data.id === rowData.id) {
        node.setData({ ...data, row_status: "error" });
      }
    });
  }

  // Uses the global `inline_success` (green) / `inline_error` (red) classes so the
  // validation message matches the organizer Setup > Voters workflow styling.
  // Also reveals the Download list button when errors are present.
  private showValidateStatus(valid: boolean, errorCount: number): void {
    if (!this.hasValidateStatusTarget) return;
    const el = this.validateStatusTarget;
    if (valid) {
      el.textContent = this.strings.valid;
      el.className = "ai-voter-list-grid-validate-status inline_success";
      // Keep download button hidden when validation passes
      if (this.hasDownloadButtonTarget) {
        this.downloadButtonTarget.hidden = true;
      }
    } else {
      const template = errorCount === 1 ? this.strings.errorsOne : this.strings.errorsOther;
      el.textContent = template.replace("%{count}", String(errorCount));
      el.className = "ai-voter-list-grid-validate-status inline_error";
      // Reveal the download button so the user can export the error list
      if (this.hasDownloadButtonTarget) {
        this.downloadButtonTarget.hidden = false;
      }
    }
  }

  private showValidateStatusError(): void {
    if (!this.hasValidateStatusTarget) return;
    const el = this.validateStatusTarget;
    el.textContent = this.strings.validateFailed;
    el.className = "ai-voter-list-grid-validate-status inline_error";
  }
}
